import axios, { AxiosError } from 'axios';

import {
  HttpError,
  HTTPErrors,
  hasPresentApiErrorCode,
} from '../../../utilities/errors/HTTPErrors';
import { isCancellationError } from '../../../utilities/errors/isCancellationError';
import type { NetworkErrorType } from '../../../utilities/errors/networkErrorType';
import { classifyNetworkError } from '../../api/network/classifyNetworkError';
import { getNetworkErrorMessage } from '../../api/network/errorMessages';
import { MAX_TRANSPORT_RETRIES } from '../../api/network/retryConfig';
import { readRequestMetadata } from '../../api/network/types';
import { getLogger, LogCategory, LogEvent, LogLevel } from '../../logger';

const MAX_BODY_SNIPPET = 500;

const logger = getLogger('axios-fetch');

/**
 * Axios `headers.get()` can return `string | number | boolean | string[] | AxiosHeaders`, not only
 * strings — normalize to a single id string for messages and correlation.
 */
function requestIdFromHeaderValue(value: unknown): string | undefined {
  if (value == null || typeof value === 'boolean') {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmedVal = value.trim();
    return trimmedVal.length > 0 ? trimmedVal : undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === 'string') {
        const trimmedVal = item.trim();
        if (trimmedVal.length > 0) {
          return trimmedVal;
        }
      }
    }
    return undefined;
  }
  return undefined;
}

/** `X-Request-Id` from the response (header or our JSON envelope), for log correlation. */
function requestIdFrom(error: AxiosError): string | undefined {
  const headers = error.response?.headers;
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }

  if (typeof headers.get === 'function') {
    const raw = headers.get('x-request-id') ?? headers.get('X-Request-Id');
    return requestIdFromHeaderValue(raw);
  }

  const raw = headers as Record<string, string | string[] | undefined>;
  const v = raw['x-request-id'] ?? raw['X-Request-Id'];
  if (typeof v === 'string' && v.length > 0) {
    return v;
  }
  if (Array.isArray(v) && typeof v[0] === 'string') {
    return v[0];
  }
  return undefined;
}

/** Non-JSON bodies (HTML, plain text, etc.) — trimmed for error messages. */
function snippetFromResponseData(data: unknown): string | undefined {
  if (typeof data !== 'string') {
    return undefined;
  }
  const t = data.trim();
  if (t.length === 0) {
    return undefined;
  }
  return t.length <= MAX_BODY_SNIPPET ? t : `${t.slice(0, MAX_BODY_SNIPPET)}…`;
}

function apiMessageFromJsonBody(data: unknown): string | undefined {
  if (data == null || typeof data !== 'object') {
    return undefined;
  }
  const msg = (data as Record<string, unknown>)['message'];
  if (typeof msg === 'string') {
    const t = msg.trim();
    return t.length > 0 ? t : undefined;
  }
  if (typeof msg === 'number' && !Number.isNaN(msg)) {
    return String(msg);
  }
  return undefined;
}

function httpStatusFallbackFrom(error: AxiosError): number {
  return error.response?.status ?? 500;
}

function httpErrorFromPlainObjectPayload(
  payload: Record<string, unknown>,
  error: AxiosError,
): HttpError {
  return HttpError.fromPlainObject(payload, {
    httpStatusFallback: httpStatusFallbackFrom(error),
  });
}

function messageFromUnknown(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmedVal = value.trim();
    return trimmedVal.length > 0 ? trimmedVal : undefined;
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return String(value);
  }
  return undefined;
}

/**
 * Best-effort user-facing message from JSON error bodies that omit a numeric `code`.
 */
function messageFromUnstructuredResponseData(
  data: Record<string, unknown>,
  nestedError?: Record<string, unknown>,
): string | undefined {
  if (nestedError) {
    const nestedMessage =
      messageFromUnknown(nestedError['message']) ??
      messageFromUnknown(nestedError['error_description']) ??
      messageFromUnknown(nestedError['errorMessage']);
    if (nestedMessage) {
      return nestedMessage;
    }
  }

  const topLevelError = data['error'];
  if (typeof topLevelError === 'string') {
    return (
      messageFromUnknown(data['error_description']) ??
      messageFromUnknown(topLevelError)
    );
  }

  return (
    apiMessageFromJsonBody(data) ?? messageFromUnknown(data['errorMessage'])
  );
}

function logUnstructuredApiErrorResponse(
  error: AxiosError,
  apiUrl: string,
  responseData: Record<string, unknown>,
): void {
  const id = requestIdFrom(error);
  const status = error.response?.status;

  logger.contextualLog(
    LogLevel.WARN,
    'Axios error: API response without structured error code (full body logged)',
    undefined,
    {
      category: LogCategory.API,
      event: LogEvent.API_ERROR,
      ...(id ? { correlationId: id } : {}),
      apiUrl,
      ...(typeof status === 'number' ? { httpStatus: status } : {}),
      responseData,
    },
  );
}

/**
 * External or legacy APIs often return `{ error: { message } }` without InSafe's numeric
 * `code`. Preserve the HTTP status and extracted message, and log the full body for ops.
 */
function httpErrorFromUnstructuredResponseData(
  error: AxiosError,
  apiUrl: string,
  data: Record<string, unknown>,
  nestedError?: Record<string, unknown>,
): HttpError {
  logUnstructuredApiErrorResponse(error, apiUrl, data);

  const status = error.response?.status ?? 500;
  const message =
    messageFromUnstructuredResponseData(data, nestedError) ??
    `Request failed with status ${status}`;

  const name =
    (nestedError && typeof nestedError['name'] === 'string'
      ? nestedError['name']
      : undefined) ?? 'UnstructuredApiError';

  const rid = data['requestId'];
  const requestId =
    typeof rid === 'string' && rid.length > 0 ? rid : requestIdFrom(error);

  return HTTPErrors.custom(
    status,
    message,
    name,
    requestId ? { requestId } : undefined,
  );
}

/**
 * Builds an {@link HttpError} with a generic user-facing summary plus correlation.
 * Raw string response bodies are logged only — not embedded in the message — to
 * avoid leaking HTML or sensitive data into UI / support channels.
 */
function httpErrorWithDiagnostics(
  error: AxiosError,
  apiUrl: string,
  status: number,
  errorName: string,
  summary: string,
): HttpError {
  const id = requestIdFrom(error);
  const snippet = snippetFromResponseData(error.response?.data);
  const apiMessage = apiMessageFromJsonBody(error.response?.data);

  if (snippet) {
    logger.contextualLog(
      LogLevel.WARN,
      'Axios error: non-JSON response body (diagnostic snippet only)',
      undefined,
      {
        category: LogCategory.API,
        event: LogEvent.API_ERROR,
        ...(id ? { correlationId: id } : {}),
        rawBody: snippet,
        apiUrl,
        httpStatus: status,
      },
    );
  }

  return HTTPErrors.custom(
    status,
    apiMessage ?? summary,
    errorName,
    id ? { requestId: id } : undefined,
  );
}

/**
 * Logs Axios failures where there is no usable HTTP response (or we are handling by code only).
 * This complements {@link httpErrorWithDiagnostics}, which only runs when the server returned a
 * status and body — e.g. `ERR_NETWORK` never reaches that path because `error.response` is absent.
 */
function transportRetriesExhausted(error: AxiosError): boolean {
  const retryCount =
    (error.config
      ? readRequestMetadata(error.config)?.retryCount
      : undefined) ?? 0;
  return retryCount >= MAX_TRANSPORT_RETRIES;
}

function httpErrorOptionsForNetworkFailure(
  error: AxiosError,
  networkErrorType: NetworkErrorType,
): { isAutoRetryable: boolean; networkErrorType: NetworkErrorType } {
  return {
    networkErrorType,
    isAutoRetryable: !transportRetriesExhausted(error),
  };
}

function networkHttpError(
  error: AxiosError,
  status: number,
  errorName: string,
  networkErrorType: NetworkErrorType,
): HttpError {
  return HTTPErrors.custom(
    status,
    getNetworkErrorMessage(networkErrorType),
    errorName,
    httpErrorOptionsForNetworkFailure(error, networkErrorType),
  );
}

function logAxiosTransportFailure(
  error: AxiosError,
  apiUrl: string,
  message: string,
): void {
  logger.contextualLog(
    LogLevel.WARN,
    `[Axios transport failure] ${message}`,
    undefined,
    {
      category: LogCategory.API,
      event: LogEvent.API_ERROR,
      apiUrl,
      axiosCode: error.code,
      axiosMessage: error.message,
    },
  );
}

/**
 * SAP / Aramco use `logID` for correlation; map it onto `requestId` and append it to
 * `message` so it survives on HttpError and appears when logged.
 */
function applySapLogIdToPayload(payload: Record<string, unknown>): void {
  const logId = payload['logID'] ?? payload['logId'];
  if (typeof logId !== 'string' || logId.trim().length === 0) {
    return;
  }

  const trimmedLogId = logId.trim();
  if (typeof payload['requestId'] !== 'string' || !payload['requestId']) {
    payload['requestId'] = trimmedLogId;
  }

  const message = payload['message'];
  if (
    typeof message === 'string' &&
    message.length > 0 &&
    !message.includes(trimmedLogId)
  ) {
    payload['message'] = `${message} (logID: ${trimmedLogId})`;
  }
}

/**
 * External or legacy APIs often return `{ error: { message } }` without a `code`, or
 * `{ error: { code: "401", message } }` with string codes (SAP). Structured bodies with
 * a `code` field are parsed via {@link HttpError.fromPlainObject}.
 */
function structuredErrorFromResponseData(
  data: Record<string, unknown>,
  error: AxiosError,
  apiUrl: string,
): HttpError | null {
  const httpStatus = httpStatusFallbackFrom(error);
  const nested = data['error'];

  if (nested && typeof nested === 'object' && nested !== null) {
    const payload = { ...(nested as Record<string, unknown>) };
    const rid = data['requestId'];
    if (typeof rid === 'string' && rid.length > 0) {
      payload['requestId'] = rid;
    }

    applySapLogIdToPayload(payload);

    if (hasPresentApiErrorCode(payload['code'])) {
      return httpErrorFromPlainObjectPayload(payload, error);
    }
    return httpErrorFromUnstructuredResponseData(
      error,
      apiUrl,
      data,
      nested as Record<string, unknown>,
    );
  }

  if (typeof nested === 'string' && nested.trim().length > 0) {
    return httpErrorFromUnstructuredResponseData(error, apiUrl, data);
  }

  const code = data['code'];
  const msg = data['message'];
  const looksLikeFlatApiError =
    hasPresentApiErrorCode(code) &&
    (typeof msg === 'string' || typeof msg === 'number' || msg != null);

  if (looksLikeFlatApiError) {
    return HttpError.fromPlainObject(data, { httpStatusFallback: httpStatus });
  }

  return null;
}

/**
 * Handles axios errors with comprehensive categorization
 *
 * This function:
 * 1. Preserves cancellation errors for React Query to handle silently
 * 2. Categorizes axios errors into appropriate HTTP error types
 * 3. Provides detailed, actionable error messages
 *
 * @param error - The error caught from axios request
 * @param apiUrl - The URL that was being fetched (for context in error messages)
 * @returns Never returns — always throws either the original cancellation error or a categorized HttpError.
 * @throws Original error if it's a cancellation (for React Query silent handling).
 * @throws HttpError with proper categorization for all other errors.
 */
export function handleAxiosError(error: unknown, apiUrl: string): never {
  // 1. Handle cancellation errors - these should be preserved for React Query
  // React Query expects raw cancellation errors to handle them silently (no error UI)
  if (axios.isCancel(error)) {
    throw error; // Let React Query handle cancellation silently
  }

  // Check for ERR_CANCELED (explicit cancellation, not timeout)
  if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
    throw error; // Let React Query handle cancellation silently
  }

  // Safari/WebKit torn-down requests may surface as TypeError, not AxiosError
  if (!axios.isAxiosError(error) && isCancellationError(error)) {
    throw error;
  }

  // 2. Categorize axios errors
  if (axios.isAxiosError(error)) {
    throw categorizeAxiosError(error, apiUrl);
  }

  // 3. Fallback for non-axios errors (shouldn't happen, but be defensive)
  throw HTTPErrors.internalError(
    `Unexpected error fetching data from: ${apiUrl}`,
    'UnexpectedError',
  );
}

/**
 * Categorizes axios errors into appropriate HTTP error types
 * Provides detailed, actionable error messages based on error type
 *
 * Priority order:
 * 1. Server-provided structured errors (use as-is)
 * 2. HTTP status codes when `response.status` is set (Axios uses ERR_BAD_REQUEST for all 4xx and
 *    ERR_BAD_RESPONSE for all 5xx, so status must win over `error.code`)
 * 3. Axios error codes (ERR_NETWORK, ETIMEDOUT, ERR_BAD_REQUEST without response, etc.)
 * 4. Fallback with descriptive message
 */
function categorizeAxiosError(error: AxiosError, apiUrl: string): HttpError {
  const data = error.response?.data;

  if (data && typeof data === 'object') {
    const structured = structuredErrorFromResponseData(
      data as Record<string, unknown>,
      error,
      apiUrl,
    );
    if (structured) {
      return structured;
    }
  }

  const status = error.response?.status;
  const isServerError =
    typeof status === 'number' && status >= 500 && status <= 599;
  // Non-JSON string body on 5xx only — requires a response. `ERR_NETWORK` has no `response`.
  if (isServerError && snippetFromResponseData(data)) {
    return httpErrorWithDiagnostics(
      error,
      apiUrl,
      status,
      'UnexpectedErrorBody',
      'Server returned a non-JSON error body.',
    );
  }

  if (status) {
    const statusText = error.response?.statusText || '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = error.response?.data as any;

    switch (status) {
      case 400:
        return HTTPErrors.badRequest(
          responseData?.message || 'Invalid request data.',
          'BadRequest',
        );

      case 401:
        return HTTPErrors.unauthorized(
          'Your session has expired. Please sign in again.',
          'Unauthorized',
        );

      case 403:
        return HTTPErrors.forbidden(
          'You do not have permission to access this resource.',
          'Forbidden',
        );

      case 404:
        return HTTPErrors.notFound(`Resource not found: ${apiUrl}`, 'NotFound');

      case 409:
        return HTTPErrors.custom(
          409,
          responseData?.message ||
            'A conflict occurred with the current state.',
          'Conflict',
        );

      case 422:
        return HTTPErrors.custom(
          422,
          responseData?.message ||
            'Validation failed. Please check your input.',
          'ValidationError',
        );

      case 429:
        return HTTPErrors.custom(
          429,
          'Too many requests. Please wait a moment and try again.',
          'RateLimitError',
        );

      case 500:
        return httpErrorWithDiagnostics(
          error,
          apiUrl,
          500,
          'InternalServerError',
          'An internal server error occurred. Please try again later.',
        );

      case 502:
        return networkHttpError(error, 502, 'BadGateway', 'server_error');

      case 503:
        return networkHttpError(
          error,
          503,
          'ServiceUnavailable',
          'server_error',
        );

      case 504:
        return networkHttpError(error, 504, 'GatewayTimeout', 'timeout');

      default:
        return HTTPErrors.custom(
          status,
          responseData?.message ||
            `Request failed with status ${status}: ${statusText}`,
          'HTTPError',
        );
    }
  }

  switch (error.code) {
    case 'ERR_NETWORK':
      logAxiosTransportFailure(
        error,
        apiUrl,
        'Axios error: no HTTP response (network / transport failure)',
      );
      return networkHttpError(
        error,
        503,
        'NetworkError',
        classifyNetworkError(error),
      );

    case 'ECONNABORTED':
    case 'ETIMEDOUT':
      logAxiosTransportFailure(
        error,
        apiUrl,
        'Axios error: request aborted or timed out before a complete response',
      );
      return networkHttpError(error, 408, 'TimeoutError', 'timeout');

    case 'ERR_BAD_REQUEST':
      return HTTPErrors.badRequest(
        'Invalid request format. Please check your input.',
        'BadRequestError',
      );

    case 'ERR_BAD_RESPONSE':
      return networkHttpError(error, 502, 'BadResponseError', 'server_error');

    case 'ERR_INVALID_URL':
      return HTTPErrors.custom(
        400,
        `Invalid API endpoint: ${apiUrl}`,
        'InvalidURLError',
      );

    case 'ERR_FR_TOO_MANY_REDIRECTS':
      return HTTPErrors.custom(
        310,
        'Too many redirects. Please contact support.',
        'TooManyRedirectsError',
      );

    case 'ERR_NOT_SUPPORT':
      return HTTPErrors.custom(
        501,
        'Operation not supported in current environment.',
        'NotSupportedError',
      );

    case 'ERR_BAD_OPTION':
    case 'ERR_BAD_OPTION_VALUE':
      return HTTPErrors.custom(
        500,
        'Invalid request configuration. Please contact support.',
        'ConfigurationError',
      );
  }

  return HTTPErrors.internalError(
    `Failed to fetch data from: ${apiUrl}. ${error.message || 'Unknown error'}`,
    'UnknownError',
  );
}
